import { zodResolver } from '@hookform/resolvers/zod';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import { formatAbnAcn, isValidAbn, isValidAcn, normaliseState, organisationBody } from '@pulsepoint/shared';
import { MapPin, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Controller, useForm, useWatch, type FieldErrors } from 'react-hook-form';
import type { z } from 'zod';
import { ApiError } from '../../api/client';
import { useCreateOrganisation, useDeactivateSite, useSites, useUpdateOrganisation, type Organisation, type Site } from '../../api/organisations';
import { usePermission } from '../../auth/permissions';
import { DataTable, type Column } from '../../components/DataTable';
import { useDialog, type DialogProps } from '../../components/dialogs/DialogProvider';
import { FormDialog } from '../../components/dialogs/FormDialog';
import { EmptyState } from '../../components/EmptyState';
import { applyApiError } from '../../components/form/apiErrors';
import { FieldRow, FormSection } from '../../components/form/FieldRow';
import { FormSelect } from '../../components/form/FormSelect';
import { FormTextField } from '../../components/form/FormTextField';
import { RowMenu } from '../../components/RowMenu';
import { useToast } from '../../components/Toast';
import { formSaver, useLeaveGuard, useUnsavedChanges } from '../../components/unsaved/unsaved';
import { addressLine } from '../../lib/format';
import { colours } from '../../theme';
import { frequencyOptions, stateOptions, timezoneOptions } from './options';
import { SiteDialog } from './SiteDialog';

type In = z.input<typeof organisationBody>;
type Out = z.output<typeof organisationBody>;
type TabId = 'business' | 'address' | 'contacts' | 'sites';

/** Which tab each field lives on, so a problem on a hidden tab can be shown. */
const TAB_OF: Record<string, TabId> = {
  businessName: 'business', tradingAs: 'business', accountNumber: 'business', abnAcn: 'business', accountOpenedOn: 'business',
  billingFrequency: 'business', timezone: 'business', notes: 'business', powersensorEnabled: 'business', powersensorRate: 'business',
  addressLevel: 'address', addressStreet: 'address', addressSuburb: 'address', addressState: 'address', addressPostcode: 'address',
  contactName: 'contacts', contactEmail: 'contacts', contactPhone: 'contacts',
  accountsContactName: 'contacts', accountsContactEmail: 'contacts', accountsContactPhone: 'contacts',
};

function defaults(o?: Organisation): In {
  const s = (v: string | null | undefined) => v ?? '';
  return {
    businessName: s(o?.businessName), tradingAs: s(o?.tradingAs), accountNumber: s(o?.accountNumber), abnAcn: o?.abnAcn ? formatAbnAcn(o.abnAcn) : '',
    accountOpenedOn: s(o?.accountOpenedOn),
    billingFrequency: o?.billingFrequency ?? '', timezone: (o?.timezone ?? 'Australia/Sydney') as In['timezone'], notes: s(o?.notes),
    powersensorEnabled: o?.powersensorEnabled ?? false, powersensorRate: o?.powersensorRate == null ? '' : Number(o.powersensorRate).toFixed(2),
    addressLevel: s(o?.addressLevel), addressStreet: s(o?.addressStreet), addressSuburb: s(o?.addressSuburb),
    addressState: normaliseState(o?.addressState) ?? '', addressPostcode: s(o?.addressPostcode),
    contactName: s(o?.contactName), contactEmail: s(o?.contactEmail), contactPhone: s(o?.contactPhone),
    accountsContactName: s(o?.accountsContactName), accountsContactEmail: s(o?.accountsContactEmail), accountsContactPhone: s(o?.accountsContactPhone),
  } as In;
}

/**
 * Add or edit an organisation: Business details, Address, Contacts and its Sites.
 * Its first site is made with it (most organisations only ever have one); add more on Sites.
 */
export function OrganisationDialog({ organisation, initialTab = 'business', close }: { organisation?: Organisation; initialTab?: TabId } & DialogProps<Organisation>) {
  const [current, setCurrent] = useState(organisation);
  const [tab, setTab] = useState<TabId>(initialTab);
  const [error, setError] = useState('');
  const canCreate = usePermission('organisations.create');
  const canEdit = usePermission('organisations.edit');
  const canPowersensor = usePermission('powersensor.edit');
  const readOnly = current ? !canEdit : !canCreate;
  const create = useCreateOrganisation();
  const update = useUpdateOrganisation();
  const toast = useToast();

  const form = useForm<In, unknown, Out>({ resolver: zodResolver(organisationBody), defaultValues: defaults(organisation), disabled: readOnly });
  const errs = form.formState.errors;
  const powersensorOn = useWatch({ control: form.control, name: 'powersensorEnabled' });
  // The ATO check digit: a warning only, so a number can still be saved.
  const abn = String(useWatch({ control: form.control, name: 'abnAcn' }) ?? '').replace(/\s+/g, '');
  const abnWarning = (abn.length === 11 && !isValidAbn(abn)) || (abn.length === 9 && !isValidAcn(abn)) ? `Check this number: it doesn't pass the ATO's ${abn.length === 11 ? 'ABN' : 'ACN'} check` : undefined;
  const tabHasError = (t: TabId) => Object.keys(errs).some((k) => TAB_OF[k] === t);

  /** Opens the tab with the first problem, so it isn't hidden on another tab. */
  const showFirstProblem = (fields: string[]) => {
    const first = Object.keys(TAB_OF).find((k) => fields.includes(k));
    if (first) setTab(TAB_OF[first]!);
  };

  const submit = formSaver(form, async (v) => {
    setError('');
    try {
      if (current) {
        const saved = await update.mutateAsync({ id: current.id, ...v });
        toast(`${saved.businessName} was saved.`);
        close(saved);
      } else {
        const saved = await create.mutateAsync(v);
        // Its first site is made with it; more can be added on the Sites tab.
        toast(`${saved.businessName} was added.`);
        close(saved);
      }
    } catch (e) {
      const msg = applyApiError(e, form.setError, Object.keys(TAB_OF));
      setError(msg);
      if (e instanceof ApiError) showFirstProblem(Object.keys(e.fields));
      return false;
    }
  }, (e: FieldErrors) => showFirstProblem(Object.keys(e)));

  const tabLabel = (t: TabId, label: string) => (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
      {label}
      {tabHasError(t) && <Box component="span" aria-label="has a problem" sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: colours.faultRed }} />}
    </Box>
  );
  useUnsavedChanges({ dirty: form.formState.isDirty, save: submit, discard: () => form.reset() });
  // A tab's own forms (cards, billing, odometer) are lost when you switch away: ask first.
  const guard = useLeaveGuard();

  return (
    <FormDialog
      title={current ? current.businessName : 'Add an organisation'}
      submitLabel={current ? 'Save changes' : 'Add the organisation'}
      onSubmit={submit}
      onClose={() => close(current)}
      busy={create.isPending || update.isPending}
      error={error}
      maxWidth="md"
      minContentHeight={440}
      readOnly={readOnly || (tab === 'sites' && !!current && !form.formState.isDirty)}
      tabs={
        <Tabs value={tab} onChange={(_e, t: TabId) => guard.switchTab(() => setTab(t))} variant="scrollable" allowScrollButtonsMobile aria-label="Organisation sections">
          <Tab value="business" label={tabLabel('business', 'Business details')} />
          <Tab value="address" label={tabLabel('address', 'Address')} />
          <Tab value="contacts" label={tabLabel('contacts', 'Contacts')} />
          <Tab value="sites" label={current ? `Sites (${current.siteCount})` : 'Sites'} />
        </Tabs>
      }
    >
      {/* Every tab stays mounted, so values and errors survive switching tabs. */}
      <Box sx={{ display: tab === 'business' ? 'grid' : 'none', gap: 2.5 }}>
        <FieldRow>
          <FormTextField control={form.control} name="businessName" label="Business name" autoFocus={!current && !readOnly} />
          <FormTextField control={form.control} name="tradingAs" label="Trading as" />
        </FieldRow>
        <FieldRow>
          <FormTextField control={form.control} name="accountNumber" label="Account number" />
          <FormTextField
            control={form.control}
            name="abnAcn"
            label="ABN or ACN"
            placeholder="XX XXX XXX XXX"
            // Spaced as the ATO writes them once you leave the field: 51 824 753 556, or 004 085 616 for an ACN.
            onBlur={(e) => {
              const v = e.target.value;
              if (v && formatAbnAcn(v) !== v) form.setValue('abnAcn', formatAbnAcn(v), { shouldDirty: true });
            }}
            helperText={abnWarning}
            slotProps={{ htmlInput: { inputMode: 'numeric' } }}
          />
        </FieldRow>
        <FieldRow>
          <FormTextField control={form.control} name="accountOpenedOn" label="Account opened" type="date" slotProps={{ inputLabel: { shrink: true } }} />
          <FormSelect control={form.control} name="billingFrequency" label="Billing frequency" options={frequencyOptions} emptyLabel="Not set" />
        </FieldRow>
        <FieldRow>
          <FormSelect control={form.control} name="timezone" label="Time zone" options={timezoneOptions} helperText="For dates on reports and bills" />
        </FieldRow>
        <FormTextField control={form.control} name="notes" label="Notes" multiline minRows={2} />
        {(canPowersensor || current?.powersensorEnabled) && (
          <FormSection title="Powersensor">
            <Controller
              control={form.control}
              name="powersensorEnabled"
              render={({ field }) => (
                <FormControlLabel
                  control={<Switch checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} disabled={field.disabled || !canPowersensor} />}
                  label="Enable Powersensor"
                />
              )}
            />
            {powersensorOn ? (
              <FieldRow>
                <FormTextField
                  control={form.control}
                  name="powersensorRate"
                  label="Driver reimbursement rate ($ per kWh)"
                  placeholder="e.g. 0.30"
                  helperText="What drivers are paid back for charging at home, on the Powersensor report"
                  disabled={!canPowersensor}
                  slotProps={{ htmlInput: { inputMode: 'decimal' } }}
                />
              </FieldRow>
            ) : (
              <Typography variant="body2" color="textSecondary">
                Off: nothing about Powersensor shows for this organisation, and its drivers’ readings aren’t downloaded.
              </Typography>
            )}
          </FormSection>
        )}
      </Box>

      <Box sx={{ display: tab === 'address' ? 'grid' : 'none', gap: 2.5 }}>
        <FieldRow columns="1fr 3fr">
          <FormTextField control={form.control} name="addressLevel" label="Level, suite or unit" />
          <FormTextField control={form.control} name="addressStreet" label="Street address" />
        </FieldRow>
        <FieldRow columns="2fr 1fr 1fr">
          <FormTextField control={form.control} name="addressSuburb" label="Suburb" />
          <FormSelect control={form.control} name="addressState" label="State" options={stateOptions} emptyLabel="Not set" />
          <FormTextField control={form.control} name="addressPostcode" label="Postcode" slotProps={{ htmlInput: { inputMode: 'numeric', maxLength: 4 } }} />
        </FieldRow>
        <Typography variant="body2" color="textSecondary">The business address, for invoices. Each site has its own address.</Typography>
      </Box>

      <Box sx={{ display: tab === 'contacts' ? 'grid' : 'none', gap: 4 }}>
        <FormSection title="Main contact">
          <FieldRow>
            <FormTextField control={form.control} name="contactName" label="Name" />
            <FormTextField control={form.control} name="contactPhone" label="Phone" type="tel" />
          </FieldRow>
          <FormTextField control={form.control} name="contactEmail" label="Email" type="email" />
        </FormSection>
        <FormSection title="Accounts contact">
          <FieldRow>
            <FormTextField control={form.control} name="accountsContactName" label="Name" />
            <FormTextField control={form.control} name="accountsContactPhone" label="Phone" type="tel" />
          </FieldRow>
          <FormTextField control={form.control} name="accountsContactEmail" label="Email" type="email" helperText="Invoices and statements go here" />
        </FormSection>
      </Box>

      {tab === 'sites' &&
        (current ? (
          <SitesPanel organisation={current} onCountChange={(n) => setCurrent((c) => (c ? { ...c, siteCount: n } : c))} />
        ) : (
          <EmptyState title="Add the organisation first">Then you can add its sites: the places where it has chargers.</EmptyState>
        ))}
    </FormDialog>
  );
}

/** The organisation's sites, each opened in its own dialog. */
function SitesPanel({ organisation: o, onCountChange }: { organisation: Organisation; onCountChange(n: number): void }) {
  const sites = useSites(o.id);
  const canEdit = usePermission('sites.edit');
  const dialog = useDialog();
  const toast = useToast();
  const deactivate = useDeactivateSite();
  const props = { organisationId: o.id, organisationName: o.businessName, organisationTimezone: o.timezone };

  const refreshCount = async (p: Promise<unknown>) => {
    await p;
    const fresh = await sites.refetch();
    if (fresh.data) onCountChange(fresh.data.length);
  };

  const columns: Column<Site>[] = [
    {
      key: 'name',
      header: 'Site',
      sortValue: (s) => s.name.toLowerCase(),
      render: (s) => (
        <Box>
          <Typography sx={{ fontWeight: 500 }}>{s.name}</Typography>
          <Typography variant="body2" color="textSecondary">{addressLine(s) || 'No address yet'}</Typography>
        </Box>
      ),
    },
    { key: 'chargers', header: 'Chargers', align: 'right', sortValue: (s) => s.chargerCount, render: (s) => <span className="num">{s.chargerCount}</span> },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 2 }}>
        <Typography color="textSecondary">The places where {o.businessName} has chargers.</Typography>
        {canEdit && (
          <Button startIcon={<Plus size={18} strokeWidth={1.75} />} onClick={() => void refreshCount(dialog.open(SiteDialog, props))}>
            Add a site
          </Button>
        )}
      </Box>
      <DataTable
        label="Sites"
        rows={sites.data}
        columns={columns}
        rowKey={(s) => s.id}
        loading={sites.isPending}
        error={sites.error}
        initialSort={{ key: 'name', direction: 'asc' }}
        pageSize={50}
        onRowClick={(s) => void refreshCount(dialog.open(SiteDialog, { ...props, site: s, readOnly: !canEdit }))}
        rowActions={
          canEdit
            ? (s) => (
                <RowMenu
                  label={`Actions for ${s.name}`}
                  items={[
                    { label: 'Edit', icon: Pencil, onClick: () => void refreshCount(dialog.open(SiteDialog, { ...props, site: s })) },
                    {
                      label: 'Remove',
                      icon: Trash2,
                      danger: true,
                      onClick: async () => {
                        if (s.chargerCount > 0) {
                          toast(`${s.name} has ${s.chargerCount === 1 ? 'a charger' : `${s.chargerCount} chargers`}. Move or remove them first.`, 'error');
                          return;
                        }
                        const ok = await dialog.confirm({ title: `Remove ${s.name}?`, message: 'It disappears from lists. Its past sessions stay in reports.', confirmLabel: 'Remove', danger: true });
                        if (!ok) return;
                        try {
                          await refreshCount(deactivate.mutateAsync(s.id));
                          toast(`${s.name} was removed.`);
                        } catch (e) {
                          toast(e instanceof ApiError ? e.message : 'Something went wrong.', 'error');
                        }
                      },
                    },
                  ]}
                />
              )
            : undefined
        }
        empty={
          <EmptyState title="No sites yet" action={canEdit && <Button startIcon={<MapPin size={18} strokeWidth={1.75} />} onClick={() => void refreshCount(dialog.open(SiteDialog, props))}>Add the first site</Button>}>
            A site is a place with chargers: an office car park, an apartment building, a depot.
          </EmptyState>
        }
      />
    </Box>
  );
}
